/*
  Shared helpers available to every component as window.Theme.
  Values that come from Liquid (routes, money format, strings) are read from the
  JSON block with id "theme-config" rendered in layout/theme.liquid.
*/
(function () {
  const configElement = document.getElementById('theme-config');
  const config = configElement ? JSON.parse(configElement.textContent) : {};

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  let trap = null;

  /*
    Translated strings arrive HTML-escaped (an apostrophe becomes &#39;). Text set
    from scripts is not parsed as HTML, so it is decoded once here.
  */
  const decoder = document.createElement('textarea');
  function decode(text) {
    if (typeof text !== 'string' || text.indexOf('&') === -1) return text;
    decoder.innerHTML = text;
    return decoder.value;
  }

  function decodeAll(node) {
    if (typeof node === 'string') return decode(node);
    if (!node || typeof node !== 'object') return node;
    Object.keys(node).forEach((key) => {
      node[key] = decodeAll(node[key]);
    });
    return node;
  }

  const Theme = {
    routes: config.routes || {},
    strings: decodeAll(config.strings || {}),
    decode,
    settings: config.settings || {},
    moneyFormat: config.moneyFormat || '${{amount}}',

    /* Formats a price in cents with the shop's money format. */
    formatMoney(cents) {
      const value = Number(cents) / 100;
      const group = (number, decimals, thousands, decimal) => {
        const parts = number.toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return group(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return group(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return group(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return group(value, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return group(value, 0, ' ', ',');
          case 'amount_with_space_separator':
            return group(value, 2, ' ', ',');
          default:
            return group(value, 2, ',', '.');
        }
      });
    },

    /* Fetches one section rendered for the given URL and returns its HTML. */
    async fetchSection(url, sectionId, options = {}) {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', sectionId);
      const response = await fetch(target.toString(), { signal: options.signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      return response.text();
    },

    /* Replaces the element matching selector with the same element from html. */
    replaceFrom(html, selector, root = document) {
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      const destination = root.querySelector(selector);
      if (!source || !destination) return false;
      destination.replaceWith(source);
      return true;
    },

    /* JSON request helper for the Ajax Cart API. */
    async postJSON(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.description || data.message || 'Request failed');
        error.data = data;
        throw error;
      }
      return data;
    },

    emit(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    },

    on(name, handler) {
      document.addEventListener(name, handler);
      return () => document.removeEventListener(name, handler);
    },

    debounce(fn, wait = 250) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
      };
    },

    /* Keeps keyboard focus inside container until releaseFocus is called. */
    trapFocus(container, initialFocus) {
      this.releaseFocus(false);
      const returnTo = document.activeElement;
      const onKeydown = (event) => {
        if (event.key !== 'Tab') return;
        const items = Array.from(container.querySelectorAll(focusableSelector)).filter(
          (element) => element.offsetParent !== null
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      container.addEventListener('keydown', onKeydown);
      trap = { container, onKeydown, returnTo };
      const target = initialFocus || container.querySelector(focusableSelector) || container;
      if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      target.focus();
    },

    releaseFocus(restore = true) {
      if (!trap) return;
      trap.container.removeEventListener('keydown', trap.onKeydown);
      const { returnTo } = trap;
      trap = null;
      if (restore && returnTo && typeof returnTo.focus === 'function') returnTo.focus();
    },

    lockScroll() {
      document.body.classList.add('has-open-overlay');
    },

    unlockScroll() {
      document.body.classList.remove('has-open-overlay');
    },

    /* Reads a value from localStorage without throwing in private modes. */
    storageGet(key) {
      try {
        return JSON.parse(window.localStorage.getItem(key));
      } catch (error) {
        return null;
      }
    },

    storageSet(key, value) {
      try {
        if (value === null || value === undefined) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* storage unavailable: the feature degrades to per-page state */
      }
    },
  };

  window.Theme = Theme;

  /* Keeps the header cart count in sync on every page. */
  Theme.on('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = cart.item_count === 0;
    });
  });
})();
